import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useFeature } from '../hooks/useFeature';
import SignupLinkSettings from '../components/SignupLinkSettings';
import './ClubSettings.css';

const ClubSettings = () => {
  const bgOn = useFeature('bgNumbers');
  const joinLinkOn = useFeature('joinLink');
  const { user, can } = useAuth();
  const managesSettings = can('settings', 'MANAGE');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });
  const [automationSettings, setAutomationSettings] = useState({
    emailEnabled: true,
    certificateEmailEnabled: true,
    sessionReminderEnabled: true,
    membershipReminderEnabled: true,
    inactivityWarningEnabled: true,
  });
  const [business, setBusiness] = useState({ legalName: '', vatNumber: '', companyNumber: '', bankDetails: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [linkMessage, setLinkMessage] = useState('');
  const [clubLink, setClubLink] = useState({ emailName: null, isHouseClub: false });

  const fetchClubSettings = useCallback(async () => {
    try {
      const response = await axios.get(`/api/clubs/${user.clubId}`);
      setEmailStatus(response.data.emailStatus || null);
      setFormData({
        name: response.data.name || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        email: response.data.email || ''
      });
      setAutomationSettings({
        emailEnabled: response.data.emailEnabled ?? true,
        certificateEmailEnabled: response.data.certificateEmailEnabled ?? true,
        sessionReminderEnabled: response.data.sessionReminderEnabled ?? true,
        membershipReminderEnabled: response.data.membershipReminderEnabled ?? true,
        inactivityWarningEnabled: response.data.inactivityWarningEnabled ?? true,
        bgReportSenders: response.data.bgReportSenders || '',
        signupsOpen: !!response.data.signupsOpen,
        policiesUrl: response.data.policiesUrl || '',
      });
      setClubLink({ emailName: response.data.emailName || null, isHouseClub: !!response.data.isHouseClub });
      const biz = await axios.get(`/api/clubs/${user.clubId}/business`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setBusiness({
        legalName: biz.data.legalName || '',
        vatNumber: biz.data.vatNumber || '',
        companyNumber: biz.data.companyNumber || '',
        bankDetails: biz.data.bankDetails || '',
      });
    } catch (error) {
      console.error('Failed to fetch club settings:', error);
      setError('Failed to load club settings');
    } finally {
      setLoading(false);
    }
  }, [user.clubId]);

  useEffect(() => {
    if (!managesSettings) {
      setError('Access denied. You need Manage access to settings to change club settings.');
      setLoading(false);
      return;
    }

    fetchClubSettings();
  }, [managesSettings, fetchClubSettings]);

  const updateSetting = (key, value) => {
    setAutomationSettings(prev => ({ ...prev, [key]: value }));
    if (success) setSuccess('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear success message when user makes changes
    if (success) setSuccess('');
  };

  const handleBusinessChange = (e) => {
    const { name, value } = e.target;
    setBusiness(prev => ({ ...prev, [name]: value }));
    if (success) setSuccess('');
  };

  const sendLink = async () => {
    setLinkMessage('');
    try {
      const r = await axios.post(`/api/clubs/${user.clubId}/email/confirm`);
      setEmailStatus(r.data);
      setLinkMessage('Sent.');
    } catch (err) {
      setLinkMessage(err.response?.data?.error || "Couldn't send it.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const saved = await axios.put(`/api/clubs/${user.clubId}`, formData);
      setEmailStatus(saved.data.club?.emailStatus || null);
      await axios.patch(`/api/clubs/${user.clubId}/settings`, automationSettings, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      await axios.patch(`/api/clubs/${user.clubId}/business`, business, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSuccess('Club settings updated successfully!');
    } catch (error) {
      console.error('Failed to update club settings:', error);
      setError(error.response?.data?.error || 'Failed to update club settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading club settings...</p>
      </div>
    );
  }

  if (!managesSettings) {
    return (
      <div className="alert alert-error">
        Access denied. You need Manage access to settings to change club settings.
      </div>
    );
  }

  return (
    <div>
      <div className="flex-between">
        <h1>Club Settings</h1>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Basic Information</h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Club Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter your club name"
              required
            />

          </div>

          <div className="form-group">
            <label htmlFor="address" className="form-label">
              Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter your club address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter your club phone number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter your club email address"
            />
            <small className="text-muted" style={{ display: 'block', marginTop: '0.35rem' }}>
              Replies to the club's emails go here. A new address has to be confirmed from a link we email to it before the club can send email.
            </small>
            {emailStatus?.email && emailStatus.email === formData.email.trim() && (
              emailStatus.verified ? (
                <p style={{ margin: '0.5rem 0 0', color: 'var(--booking-success, #1e7b3a)', fontWeight: 600 }}>✓ Confirmed</p>
              ) : (
                <div style={{ marginTop: '0.5rem', padding: '0.75rem', borderRadius: 8, background: '#fff4e0', color: '#7a4a00' }}>
                  <strong>Not confirmed yet.</strong>{' '}
                  {emailStatus.sentAt ? <>We sent a link to {emailStatus.email}. Open it to confirm. Check spam if it isn't there.</> : <>Send a link to {emailStatus.email} to confirm it.</>}
                  {' '}No emails go out until it's confirmed.
                  <div style={{ marginTop: '0.5rem' }}>
                    <button type="button" className="btn btn-sm btn-outline" style={{ width: 'auto', display: 'inline-block' }} onClick={sendLink}>Send the link again</button>
                    {linkMessage && <span style={{ marginLeft: '0.5rem' }}>{linkMessage}</span>}
                  </div>
                </div>
              )
            )}
          </div>




          <div className="form-group">
            <label className="form-label">Email &amp; Automation Settings</label>
            <div>
              <label>
                <input type="checkbox" checked={automationSettings.emailEnabled}
                  disabled={!automationSettings.emailEnabled && !emailStatus?.verified}
                  onChange={e => updateSetting('emailEnabled', e.target.checked)} />
                {' '}Enable email sending for this club
                {!automationSettings.emailEnabled && !emailStatus?.verified && <span className="text-muted"> (confirm the club's email address first)</span>}
              </label>
            </div>
            <div>
              <label>
                <input type="checkbox" checked={automationSettings.certificateEmailEnabled ?? true}
                  onChange={e => updateSetting('certificateEmailEnabled', e.target.checked)}
                  disabled={!automationSettings.emailEnabled} />
                {' '}Send certificate award notifications to parents/guardians
              </label>
            </div>
            <div>
              <label>
                <input type="checkbox" checked={automationSettings.sessionReminderEnabled ?? true}
                  onChange={e => updateSetting('sessionReminderEnabled', e.target.checked)} />
                {' '}Send session reminders (day before, when slots available)
              </label>
            </div>
            <div>
              <label>
                <input type="checkbox" checked={automationSettings.membershipReminderEnabled ?? true}
                  onChange={e => updateSetting('membershipReminderEnabled', e.target.checked)} />
                {' '}Send membership payment reminders (3 days before renewal)
              </label>
            </div>
            <div>
              <label>
                <input type="checkbox" checked={automationSettings.inactivityWarningEnabled ?? true}
                  onChange={e => updateSetting('inactivityWarningEnabled', e.target.checked)} />
                {' '}Warn and delete inactive members after 6 months
              </label>
            </div>
            {bgOn && <div style={{ marginTop: '0.75rem' }}>
              <label htmlFor="bg-report-senders" className="form-label" style={{ fontWeight: 400 }}>
                British Gymnastics lapsed members reports come from
              </label>
              <input
                id="bg-report-senders"
                type="text"
                className="form-control"
                placeholder="e.g. coach@outlook.com"
                value={automationSettings.bgReportSenders ?? ''}
                onChange={e => updateSetting('bgReportSenders', e.target.value)}
              />
              <small className="text-muted">
                The address shown as the sender when the BG report reaches the club's Gmail (check with
                "Show original" in Gmail), separated by commas if more than one. The report is read each morning
                and lapsed gymnasts are marked expired. Leave blank to turn this off.
              </small>
            </div>}
          </div>

          {joinLinkOn && <SignupLinkSettings club={clubLink} settings={automationSettings} onChange={updateSetting} />}

          <div className="form-group">
            <label className="form-label">Business details for invoices</label>
            <p className="form-help" style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--booking-text-muted, #666)' }}>
              Printed on invoices and credit notes you send to other clubs and businesses. Leave blank anything that doesn't apply.
            </p>
            <label htmlFor="legalName" className="form-label">Legal name</label>
            <input id="legalName" name="legalName" className="form-control" value={business.legalName}
              onChange={handleBusinessChange} placeholder="If different from the club name" />
            <label htmlFor="companyNumber" className="form-label">Company or charity number</label>
            <input id="companyNumber" name="companyNumber" className="form-control" value={business.companyNumber}
              onChange={handleBusinessChange} />
            <label htmlFor="vatNumber" className="form-label">VAT number</label>
            <input id="vatNumber" name="vatNumber" className="form-control" value={business.vatNumber}
              onChange={handleBusinessChange} placeholder="Shown for information only; no VAT is added" />
            <label htmlFor="bankDetails" className="form-label">Bank details</label>
            <textarea id="bankDetails" name="bankDetails" className="form-control" rows={3} value={business.bankDetails}
              onChange={handleBusinessChange} placeholder={'Account name\nSort code\nAccount number'} />
          </div>

          <div className="flex-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClubSettings; 